<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import AiAgentSuggestionsAPI from 'dashboard/api/aiAgentSuggestions';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';
import { useAlert } from 'dashboard/composables';
import { resolveAiAgentMode } from 'dashboard/helper/aiAgentMode';
import { normalizeAiResponseText } from 'dashboard/helper/aiResponseFormatter';
import {
  clampSuggestionText,
  suggestionDisplayText,
  suggestionNeedsExpand,
} from 'dashboard/helper/aiSuggestionLimits';
import ThinkingOrb from 'next/orb/ThinkingOrb.vue';

// SiteUp: sugestão minimal — texto + Enviar/Editar; Descartar/Reportar como
// ícones diretos (1 clique). Em suggest_auto busca sozinho; em suggest_manual
// mostra "Gerar resposta".
const props = defineProps({
  conversation: { type: Object, required: true },
  // Rascunho atual do editor (ReplyBox.message). Usado só para NÃO empurrar uma
  // sugestão automática por cima do que o atendente já está digitando.
  draft: { type: String, default: '' },
});

const emit = defineEmits(['use', 'send']);
const { t } = useI18n();

const suggestion = ref('');
const loading = ref(false);
const expanded = ref(false);
const reporting = ref(false);
const reason = ref('');
const submittingReport = ref(false);

const settings = computed(
  () => props.conversation?.additional_attributes?.ai_agent || {}
);

// Modo da IA desta conversa: vem FRESCO do backend (conversation.ai_agent_mode,
// config POR CAIXA). Mantém compat com override legado por-conversa.
const currentMode = computed(() => resolveAiAgentMode(props.conversation));

const notPaused = computed(() => settings.value.paused !== true);
// Auto suggestions: suggest_auto always; autonomous_suggest even when human
// reply paused autonomous sending (suggestions continue, auto-send does not).
const isSuggestAuto = computed(
  () =>
    currentMode.value === 'suggest_auto' ||
    currentMode.value === 'autonomous_suggest'
);
const isSuggestManual = computed(
  () => currentMode.value === 'suggest_manual' && notPaused.value
);
const showsSuggestionCard = computed(
  () => isSuggestAuto.value || isSuggestManual.value
);

const conversationId = computed(() => props.conversation?.id);
const lastMessage = computed(
  () => props.conversation?.last_non_activity_message
);
// message_type 0 = incoming (mensagem do lead)
const lastIncomingId = computed(() =>
  lastMessage.value?.message_type === 0 ? lastMessage.value?.id : null
);

// O atendente já está redigindo algo? Se tem rascunho com conteúdo (descontando
// espaços/tags vazias), NÃO auto-geramos por cima — só atrapalharia a digitação.
const agentIsComposing = computed(
  () => (props.draft || '').replace(/<[^>]*>/g, '').trim().length > 0
);

const POLL_INTERVAL_MS = 1500;
let pollTimer = null;
let requestVersion = 0;

const clearPoll = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
};

// The component is reused while navigating between conversations. Invalidate
// both pending HTTP responses and polls so an older suggestion cannot land on a
// newer customer message in the same conversation.
const resetSuggestionState = () => {
  requestVersion += 1;
  clearPoll();
  suggestion.value = '';
  loading.value = false;
  expanded.value = false;
  reporting.value = false;
  reason.value = '';
};

const previewText = computed(() =>
  suggestionDisplayText(suggestion.value, expanded.value)
);
const canToggleExpand = computed(() => suggestionNeedsExpand(suggestion.value));

watch(conversationId, resetSuggestionState);
watch(isSuggestAuto, enabled => {
  if (!enabled) resetSuggestionState();
});

const fetchSuggestion = async (manual = false) => {
  if (!conversationId.value) return;

  const requestId = conversationId.value;
  const requestMessageId = lastIncomingId.value;
  const requestMode = currentMode.value;
  requestVersion += 1;
  const version = requestVersion;
  const requestIsCurrent = () =>
    version === requestVersion &&
    requestId === conversationId.value &&
    requestMessageId === lastIncomingId.value &&
    requestMode === currentMode.value;

  clearPoll();
  loading.value = true;
  suggestion.value = '';
  reporting.value = false;
  let keepLoading = false;

  try {
    const { data } = await AiAgentSuggestionsAPI.suggest(conversationId.value);
    if (!requestIsCurrent()) return;

    if (data?.status === 'processing') {
      keepLoading = true;
      pollTimer = setTimeout(() => {
        pollTimer = null;
        if (requestIsCurrent()) fetchSuggestion(manual);
      }, POLL_INTERVAL_MS);
      return;
    }

    if (data?.suggestion) {
      suggestion.value = clampSuggestionText(
        normalizeAiResponseText(data.suggestion)
      );
      expanded.value = false;
    } else if (manual) {
      useAlert(t('CONVERSATION.AI_SUGGESTION.EMPTY'));
    }
  } catch (error) {
    // Auto (proativo): falha em silêncio. Manual (clique): avisa o atendente
    // com o motivo certo (rede vs servidor) — nada de "motor de IA" genérico.
    if (manual && requestIsCurrent()) {
      const data = error?.response?.data;
      useAlert(
        data?.error ||
          (error?.response
            ? t('CONVERSATION.AI_SUGGESTION.ERROR')
            : t('CONVERSATION.AI_SUGGESTION.NETWORK_ERROR'))
      );
    }
  } finally {
    if (requestIsCurrent() && !keepLoading) {
      loading.value = false;
    }
  }
};

// Sugestão AUTOMÁTICA: gera sozinho quando chega mensagem do lead, mas com
// salvaguardas pra reduzir custo de LLM e atrito:
//  - debounce: rajada de mensagens seguidas do lead coalesce numa única geração;
//  - não auto-gera se o atendente já está com rascunho no editor (composing);
//  - re-checa as condições no momento de disparar (o estado pode ter mudado
//    durante a espera do debounce — ex.: o atendente começou a digitar, ou o
//    componente foi desmontado ao trocar de conversa).
const AUTO_DEBOUNCE_MS = 800;
let destroyed = false;
const autoFetch = debounce(() => {
  if (!destroyed && isSuggestAuto.value && !agentIsComposing.value) {
    fetchSuggestion();
  }
}, AUTO_DEBOUNCE_MS);

watch(
  lastIncomingId,
  (id, oldId) => {
    if (id !== oldId) resetSuggestionState();
    if (id && id !== oldId && isSuggestAuto.value && !agentIsComposing.value) {
      autoFetch();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  destroyed = true;
  resetSuggestionState();
});

const clearSuggestion = () => {
  suggestion.value = '';
  reporting.value = false;
  reason.value = '';
  expanded.value = false;
};

const useSuggestion = () => {
  emit('use', suggestion.value);
  clearSuggestion();
};

// SiteUp: "Enviar" — manda a sugestão direto. Não limpa o card aqui: se o
// canal rejeitar (ex.: comprimento), o atendente ainda vê e reusa o texto.
// O ReplyBox chama onSuccess só após envio bem-sucedido.
const sendSuggestion = () => {
  emit('send', suggestion.value, clearSuggestion);
};

const discard = () => {
  clearSuggestion();
};

const openReport = () => {
  reporting.value = true;
};

// SiteUp: idempotencia -- desabilita no clique (evita duplo-submit gerando 2
// reports/chamadas de LLM); o toast deixa claro que a revisao e assincrona
// (job -> IA propoe correcao -> admin aprova/recusa no painel Copilot).
const submitReport = async () => {
  if (!reason.value.trim() || submittingReport.value) return;
  submittingReport.value = true;
  try {
    await ConversationAiAgentAPI.reportFeedback(conversationId.value, {
      content: suggestion.value,
      reason: reason.value.trim(),
    });
    useAlert(t('CONVERSATION.AI_SUGGESTION.REPORT_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.AI_SUGGESTION.REPORT_ERROR'));
  } finally {
    submittingReport.value = false;
    clearSuggestion();
  }
};
</script>

<template>
  <div class="contents">
    <div
      v-if="showsSuggestionCard && (loading || suggestion || isSuggestManual)"
      class="mx-1 mb-2 px-1 py-1.5"
    >
      <!-- Manual, sem rascunho ainda: botão para gerar sob demanda -->
      <button
        v-if="isSuggestManual && !suggestion && !loading"
        type="button"
        class="inline-flex items-center gap-1 rounded-md bg-n-brand px-2.5 py-1 text-xs font-medium text-white hover:opacity-90"
        @click="fetchSuggestion(true)"
      >
        <span class="i-ri-sparkling-line size-3.5" />
        {{ t('CONVERSATION.AI_SUGGESTION.GENERATE') }}
      </button>

      <div
        v-else-if="loading"
        role="status"
        aria-live="polite"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <!-- SITEUP V2 (F1.3): IA gerando sugestão → Thinking Orb -->
        <ThinkingOrb state="working" :size="20" aria-hidden="true" />
        {{ t('CONVERSATION.AI_SUGGESTION.LOADING') }}
      </div>

      <template v-else-if="suggestion">
        <!-- sparkles | texto | ícones Descartar/Reportar; abaixo Enviar / Editar -->
        <div class="flex min-w-0 items-start gap-2">
          <span
            class="i-ri-sparkling-line mt-0.5 size-3.5 shrink-0 text-n-teal-11"
            aria-hidden="true"
          />
          <span
            class="mt-0.5 h-4 w-px shrink-0 bg-n-teal-9/60"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <div
              data-testid="ai-suggestion-preview"
              class="max-h-40 min-w-0 overflow-y-auto overflow-x-hidden"
              :class="{ 'max-h-72': expanded }"
            >
              <p
                class="m-0 break-words text-sm text-n-slate-12 whitespace-pre-wrap [overflow-wrap:anywhere]"
              >
                {{ previewText }}
              </p>
            </div>
            <button
              v-if="canToggleExpand"
              type="button"
              data-testid="ai-suggestion-expand-toggle"
              class="mt-1 rounded-md px-0 py-0.5 text-xs font-medium text-n-brand hover:underline"
              @click="expanded = !expanded"
            >
              {{
                expanded
                  ? t('CONVERSATION.AI_SUGGESTION.COLLAPSE')
                  : t('CONVERSATION.AI_SUGGESTION.EXPAND')
              }}
            </button>

            <!-- modo report: o atendente explica o erro → vira correção do agente -->
            <div v-if="reporting" class="mt-2 flex min-w-0 flex-col gap-2">
              <textarea
                v-model="reason"
                rows="2"
                :placeholder="
                  t('CONVERSATION.AI_SUGGESTION.REPORT_PLACEHOLDER')
                "
                class="w-full min-w-0 rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-sm text-n-slate-12"
              />
              <div class="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  class="rounded-md bg-n-ruby-9 px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                  :disabled="!reason.trim() || submittingReport"
                  @click="submitReport"
                >
                  {{
                    submittingReport
                      ? t('CONVERSATION.AI_SUGGESTION.REPORT_SENDING')
                      : t('CONVERSATION.AI_SUGGESTION.REPORT_SEND')
                  }}
                </button>
                <button
                  type="button"
                  class="rounded-md px-2.5 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
                  @click="reporting = false"
                >
                  {{ t('CONVERSATION.AI_SUGGESTION.REPORT_CANCEL') }}
                </button>
              </div>
            </div>

            <div v-else class="mt-2 flex min-w-0 flex-wrap items-center gap-2">
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-md bg-n-brand px-2.5 py-1 text-xs font-medium text-white hover:opacity-90"
                @click="sendSuggestion"
              >
                <span class="i-ri-send-plane-line size-3.5" />
                {{ t('CONVERSATION.AI_SUGGESTION.SEND') }}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-md border border-n-weak bg-transparent px-2.5 py-1 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2"
                @click="useSuggestion"
              >
                <span class="i-ri-pencil-line size-3.5" />
                {{ t('CONVERSATION.AI_SUGGESTION.EDIT') }}
              </button>
            </div>
          </div>

          <div v-if="!reporting" class="flex shrink-0 items-center gap-0.5">
            <button
              v-tooltip.top="t('CONVERSATION.AI_SUGGESTION.DISCARD')"
              type="button"
              data-testid="ai-suggestion-discard"
              class="inline-flex size-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="t('CONVERSATION.AI_SUGGESTION.DISCARD')"
              @click="discard"
            >
              <span class="i-ri-delete-bin-line size-4" aria-hidden="true" />
            </button>
            <button
              v-tooltip.top="t('CONVERSATION.AI_SUGGESTION.REPORT')"
              type="button"
              data-testid="ai-suggestion-report"
              class="inline-flex size-7 items-center justify-center rounded-md text-n-ruby-11 hover:bg-n-ruby-9/10 hover:text-n-ruby-11"
              :aria-label="t('CONVERSATION.AI_SUGGESTION.REPORT')"
              @click="openReport"
            >
              <span class="i-ri-flag-line size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
